
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Pencil, Trash2 } from "lucide-react";
import { formatInTimeZone } from 'date-fns-tz';
import type { PrizeAllocation } from "./types";

interface AllocationsTableProps {
  allocations: PrizeAllocation[];
  onEdit: (allocation: PrizeAllocation) => void;
  onDelete: (id: number) => void;
  LoadingRow?: () => JSX.Element[];
}

export function AllocationsTable({ allocations, onEdit, onDelete, LoadingRow }: AllocationsTableProps) {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return formatInTimeZone(
      new Date(dateString),
      'Australia/Sydney',
      'dd/MM/yyyy'
    );
  };

  if (!allocations.length && !LoadingRow) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>University</TableHead>
            <TableHead>Prize</TableHead>
            <TableHead>Daily Limit</TableHead>
            <TableHead>Allocated Count</TableHead>
            <TableHead>Last Reset</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell colSpan={6} className="text-center py-6">
              No allocations found
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>University</TableHead>
          <TableHead>Prize</TableHead>
          <TableHead>Daily Limit</TableHead>
          <TableHead>Allocated Count</TableHead>
          <TableHead>Last Reset</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {LoadingRow ? (
          <LoadingRow />
        ) : (
          allocations.map((allocation) => (
            <TableRow key={allocation.id}>
              <TableCell>{allocation.universities?.name}</TableCell>
              <TableCell>{allocation.prizes?.name}</TableCell>
              <TableCell>{allocation.daily_limit}</TableCell>
              <TableCell>
                <span className={`px-2 py-1 rounded-full ${
                  allocation.allocated_count >= allocation.daily_limit 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {allocation.allocated_count} / {allocation.daily_limit}
                </span>
              </TableCell>
              <TableCell>
                {formatDate(allocation.last_reset_at)}
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(allocation)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Allocation</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this allocation? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(allocation.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
